import {
  where, documentId, arrayUnion, arrayRemove, serverTimestamp,
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query,
  writeBatch, increment, getDoc, orderBy, limit as fsLimit,
} from 'firebase/firestore';
import {
  fetchByTable,
  addDataToTable,
  updateDataById,
  deleteDocById,
  onCollectionChange,
} from '../utils';
import { db } from '../firebase';

// Single-doc fetch by id.
export const fetchTeamById = async teamId => {
  if (!teamId) return null;
  const rows = await fetchByTable({
    tableName: 'teams',
    whereCondition: where(documentId(), '==', teamId),
  });
  return rows?.[0] ?? null;
};

// `memberUserIds` is a denormalized index (captain + managers + members'
// userIds) so a single `array-contains` query returns every team the user
// is part of, regardless of role. Every add/remove keeps it in sync.
export const fetchMyTeams = async userId => {
  if (!userId) return [];
  return fetchByTable({
    tableName: 'teams',
    whereCondition: where('memberUserIds', 'array-contains', userId),
  });
};

export const onMyTeamsChange = (userId, setSnapshot) => {
  if (!userId) return () => {};
  return onCollectionChange(
    'teams',
    setSnapshot,
    where('memberUserIds', 'array-contains', userId),
  );
};

export const addTeam = async ({
  name,
  sport,
  color,
  secondaryColor,
  model,
  captain,
}) => {
  const captainMember = {
    userId: captain.uid,
    nickname: captain.nickname,
    photoURL: captain.photoURL ?? '',
    position: captain.position ?? '',
    dorsal: null,
    addedAt: Date.now(),
  };
  return addDataToTable({
    tableName: 'teams',
    data: {
      name,
      sport: sport ?? '',
      color: color ?? '',
      secondaryColor: secondaryColor ?? '',
      model: model ?? '',
      captainUserId: captain.uid,
      managerUserIds: [],
      members: [captainMember],
      memberUserIds: [captain.uid],
      createdAt: serverTimestamp(),
      createdBy: captain.uid,
    },
  });
};

export const updateTeamMeta = async (teamId, data) =>
  updateDataById({ tableName: 'teams', id: teamId, data });

export const deleteTeam = async teamId =>
  deleteDocById({ tableName: 'teams', id: teamId });

// Roster mutations — always update both `members` and the flat
// `memberUserIds` index in the same write.
export const addMemberToTeam = async (team, member) => {
  if (team.memberUserIds?.includes(member.userId)) return true;
  const memberEntry = {
    userId: member.userId,
    nickname: member.nickname,
    photoURL: member.photoURL ?? '',
    position: member.position ?? '',
    dorsal: member.dorsal ?? null,
    addedAt: Date.now(),
  };
  return updateDataById({
    tableName: 'teams',
    id: team.id,
    data: {
      members: [...(team.members ?? []), memberEntry],
      memberUserIds: arrayUnion(member.userId),
    },
  });
};

export const removeMemberFromTeam = async (team, userId) => {
  if (userId === team.captainUserId) return false;
  return updateDataById({
    tableName: 'teams',
    id: team.id,
    data: {
      members: (team.members ?? []).filter(m => m.userId !== userId),
      memberUserIds: arrayRemove(userId),
      managerUserIds: arrayRemove(userId),
    },
  });
};

export const toggleManager = async (team, userId, makeManager) =>
  updateDataById({
    tableName: 'teams',
    id: team.id,
    data: {
      managerUserIds: makeManager
        ? arrayUnion(userId)
        : arrayRemove(userId),
    },
  });

// Convenience: is `userId` allowed to edit `team` (captain or co-manager)?
export const canManageTeam = (team, userId) => {
  if (!team || !userId) return false;
  return (
    team.captainUserId === userId ||
    (team.managerUserIds ?? []).includes(userId)
  );
};

// Marks / unmarks a member as currently injured. Persists on the team doc so
// the roster row shows a lesionado tag until it's cleared. Firestore rules
// let the player themselves flip their own flag; captain/co-manager/admin can
// flip anyone's.
export const setInjured = async (team, userId, injured) =>
  updateDataById({
    tableName: 'teams',
    id: team.id,
    data: {
      injuredUserIds: injured
        ? arrayUnion(userId)
        : arrayRemove(userId),
    },
  });

// Shifts stamped with this team's id (by the bulk-invite flow). Used by the
// TeamDetail per-shift response view.
export const fetchTeamShifts = async teamId => {
  if (!teamId) return [];
  return fetchByTable({
    tableName: 'shifts',
    whereCondition: where('teamIds', 'array-contains', teamId),
  });
};

// Team-owned "ligas" (categorías/copas) — lightweight tags used to pre-fill
// price/totalPlayers/name when creating a shift from the team detail. Not
// the same as the top-level `leagues` collection (competencias con bracket
// y fixtures). Stored as a subcollection so each team keeps its own set.
export const fetchTeamLeagues = async teamId => {
  if (!teamId) return [];
  const ref = collection(db, 'teams', teamId, 'teamLeagues');
  const snap = await getDocs(ref);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const onTeamLeaguesChange = (teamId, setSnapshot) => {
  if (!teamId) return () => {};
  const ref = query(collection(db, 'teams', teamId, 'teamLeagues'));
  return onSnapshot(
    ref,
    setSnapshot,
    err => {
      if (err.code !== 'permission-denied') {
        console.error('[Firestore] teamLeagues listener:', err.code, err.message);
      }
    },
  );
};

// `place*` fields snapshot the picked venue so opening AddShiftModal from
// this league can pre-fill place, address and map coords without re-
// resolving. Stored as null when no place was picked.
const buildPlaceFields = data => ({
  placeValue: data.placeValue ?? null, // AddShiftModal picker value (f_XXX / p_XXX)
  placeName: data.placeName ?? null,
  address: data.address ?? null,
  lat: typeof data.lat === 'number' ? data.lat : null,
  lng: typeof data.lng === 'number' ? data.lng : null,
});

export const addTeamLeague = async (teamId, data, user) => {
  const ref = collection(db, 'teams', teamId, 'teamLeagues');
  return addDoc(ref, {
    name: data.name ?? '',
    description: data.description ?? '',
    defaultPrice: Number(data.defaultPrice ?? 0),
    // Per-player one-off fee to enroll in this league (independent of the
    // per-match price). Tracked separately in `payments` below.
    registrationFee: Number(data.registrationFee ?? 0),
    payments: {},
    totalPlayers: Number(data.totalPlayers ?? 0),
    substitutes: Number(data.substitutes ?? 0),
    active: data.active !== false,
    ...buildPlaceFields(data),
    createdAt: serverTimestamp(),
    createdBy: user?.uid ?? null,
  });
};

export const updateTeamLeague = async (teamId, leagueId, data) => {
  const ref = doc(db, 'teams', teamId, 'teamLeagues', leagueId);
  const payload = {};
  if (data.name !== undefined) payload.name = data.name;
  if (data.description !== undefined) payload.description = data.description;
  if (data.defaultPrice !== undefined) payload.defaultPrice = Number(data.defaultPrice);
  if (data.registrationFee !== undefined) payload.registrationFee = Number(data.registrationFee);
  if (data.totalPlayers !== undefined) payload.totalPlayers = Number(data.totalPlayers);
  if (data.substitutes !== undefined) payload.substitutes = Number(data.substitutes);
  if (data.active !== undefined) payload.active = !!data.active;
  // Place is written as a group — either the caller passes the full
  // snapshot (all present, even if null to clear) or none at all.
  if (data.placeValue !== undefined
    || data.placeName !== undefined
    || data.address !== undefined
    || data.lat !== undefined
    || data.lng !== undefined) {
    Object.assign(payload, buildPlaceFields(data));
  }
  return updateDoc(ref, payload);
};

// Mark / unmark one member's registration fee. Stored as a map on the
// league doc keyed by userId — no subcollection needed for what is a
// small per-team set. Passing `payment: null` clears the entry.
export const setTeamLeaguePayment = async (teamId, leagueId, userId, payment, actor) => {
  if (!teamId || !leagueId || !userId) return null;
  const ref = doc(db, 'teams', teamId, 'teamLeagues', leagueId);
  const fieldPath = `payments.${userId}`;
  if (!payment) {
    return updateDoc(ref, { [fieldPath]: null });
  }
  return updateDoc(ref, {
    [fieldPath]: {
      paid: true,
      payment, // 'tokens' | 'cash'
      paidAt: serverTimestamp(),
      paidBy: actor?.uid ?? null,
    },
  });
};

// Soft delete — flips `active` to false so the league doesn't show at
// shift-create time but historical shifts tagged with it still resolve
// its name in stats. Use deleteTeamLeague for a hard delete.
export const archiveTeamLeague = async (teamId, leagueId) =>
  updateTeamLeague(teamId, leagueId, { active: false });

export const deleteTeamLeague = async (teamId, leagueId) => {
  const ref = doc(db, 'teams', teamId, 'teamLeagues', leagueId);
  return deleteDoc(ref);
};

// Team-owned expense/income ledger. Persisted in a subcollection under
// the team doc so the security rule can reuse the parent captain/manager
// check. Automatic venue costs (from shift.fieldPayments) are NOT
// persisted here — they're aggregated on-the-fly in the Gastos tab.
export const onTeamExpensesChange = (teamId, setSnapshot) => {
  if (!teamId) return () => {};
  const ref = query(collection(db, 'teams', teamId, 'expenses'));
  return onSnapshot(
    ref,
    setSnapshot,
    err => {
      if (err.code !== 'permission-denied') {
        console.error('[Firestore] teamExpenses listener:', err.code, err.message);
      }
    },
  );
};

export const addTeamExpense = async (teamId, data, user) => {
  const ref = collection(db, 'teams', teamId, 'expenses');
  return addDoc(ref, {
    concept: data.concept ?? '',
    amount: Number(data.amount ?? 0),
    kind: data.kind === 'income' ? 'income' : 'expense',
    // Payment method — 'cash' (efectivo) or 'tokens' (crédito). Default cash
    // so legacy entries keep working; the UI form picks one explicitly.
    payment: data.payment === 'tokens' ? 'tokens' : 'cash',
    date: data.date ?? serverTimestamp(),
    note: data.note ?? '',
    createdAt: serverTimestamp(),
    createdBy: user?.uid ?? null,
  });
};

export const deleteTeamExpense = async (teamId, expenseId) => {
  const ref = doc(db, 'teams', teamId, 'expenses', expenseId);
  return deleteDoc(ref);
};

// -----------------------------------------------------------------------
// Team Wallet — tokens internos por equipo
// -----------------------------------------------------------------------
// Cada equipo tiene:
//   teams/{id}.walletBalance                   -> caché numérica del pozo
//   teams/{id}/memberBalances/{userId}         -> saldo por miembro (puede ser negativo)
//   teams/{id}/walletTx/{txId}                 -> ledger inmutable de movimientos
// Todos los helpers usan writeBatch para que balance + saldo miembro + tx
// se persistan atómicamente. `walletBalance` = Σ(direction=in) − Σ(direction=out).
// `memberBalance` = Σ(topup) − Σ(charges) + Σ(refunds).

const walletTxRef = teamId => collection(db, 'teams', teamId, 'walletTx');
const memberBalanceRef = (teamId, userId) =>
  doc(db, 'teams', teamId, 'memberBalances', userId);
const teamRef = teamId => doc(db, 'teams', teamId);

const buildTxDoc = ({
  type,
  direction,
  amount,
  memberId = null,
  method = null,
  relatedShiftId = null,
  relatedLeagueId = null,
  concept = null,
  note = null,
  actor,
}) => ({
  type,
  direction,
  amount: Number(amount),
  memberId,
  method,
  relatedShiftId,
  relatedLeagueId,
  concept,
  note,
  createdAt: serverTimestamp(),
  createdBy: actor?.uid ?? null,
});

// Top-up: jugador aporta plata al equipo. Suma memberBalance del jugador
// y walletBalance del equipo. Si method === 'app_tokens', debita
// users/{memberId}.tokens en el mismo batch y loguea un tokenTransactions.
export const addTeamWalletTopUp = async (
  teamId,
  memberId,
  amount,
  method,
  actor,
  note = '',
) => {
  if (!teamId || !memberId || !amount) return null;
  const amt = Number(amount);
  if (!(amt > 0)) return null;
  const batch = writeBatch(db);

  const txDoc = doc(walletTxRef(teamId));
  batch.set(txDoc, buildTxDoc({
    type: 'topup',
    direction: 'in',
    amount: amt,
    memberId,
    method,
    note,
    actor,
  }));

  batch.set(memberBalanceRef(teamId, memberId), {
    userId: memberId,
    balance: increment(amt),
    totalContributed: increment(amt),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  batch.update(teamRef(teamId), { walletBalance: increment(amt) });

  if (method === 'app_tokens') {
    const userDoc = doc(db, 'users', memberId);
    batch.update(userDoc, { tokens: increment(-amt) });
    const tokenTxDoc = doc(collection(db, 'tokenTransactions'));
    batch.set(tokenTxDoc, {
      userId: memberId,
      email: null,
      amount: -amt,
      type: 'team_topup',
      shiftId: null,
      requestId: null,
      reference: teamId,
      createdBy: actor?.uid ?? null,
      createdAt: serverTimestamp(),
    });
  }

  await batch.commit();
  return txDoc.id;
};

// Cobro por inscripción a una liga del equipo. Baja memberBalance del
// jugador y walletBalance del equipo (el dinero sale del pozo). Puede
// dejar al jugador en negativo (queda como deuda visible en Finanzas).
export const chargeMemberForLeague = async (
  teamId,
  memberId,
  leagueId,
  amount,
  actor,
  note = '',
) => {
  if (!teamId || !memberId || !leagueId || !amount) return null;
  const amt = Number(amount);
  if (!(amt > 0)) return null;
  const batch = writeBatch(db);

  const txDoc = doc(walletTxRef(teamId));
  batch.set(txDoc, buildTxDoc({
    type: 'league_payment',
    direction: 'out',
    amount: amt,
    memberId,
    method: 'team_wallet',
    relatedLeagueId: leagueId,
    note,
    actor,
  }));

  batch.set(memberBalanceRef(teamId, memberId), {
    userId: memberId,
    balance: increment(-amt),
    totalSpent: increment(amt),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  batch.update(teamRef(teamId), { walletBalance: increment(-amt) });

  // Legacy label en teamLeagues.payments para que la UI actual siga viendo
  // el pago marcado.
  const leagueDoc = doc(db, 'teams', teamId, 'teamLeagues', leagueId);
  batch.update(leagueDoc, {
    [`payments.${memberId}`]: {
      paid: true,
      payment: 'tokens',
      paidAt: serverTimestamp(),
      paidBy: actor?.uid ?? null,
    },
  });

  await batch.commit();
  return txDoc.id;
};

export const chargeMemberForShift = async (
  teamId,
  memberId,
  shiftId,
  amount,
  actor,
  note = '',
) => {
  if (!teamId || !memberId || !shiftId || !amount) return null;
  const amt = Number(amount);
  if (!(amt > 0)) return null;
  const batch = writeBatch(db);

  const txDoc = doc(walletTxRef(teamId));
  batch.set(txDoc, buildTxDoc({
    type: 'match_payment',
    direction: 'out',
    amount: amt,
    memberId,
    method: 'team_wallet',
    relatedShiftId: shiftId,
    note,
    actor,
  }));

  batch.set(memberBalanceRef(teamId, memberId), {
    userId: memberId,
    balance: increment(-amt),
    totalSpent: increment(amt),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  batch.update(teamRef(teamId), { walletBalance: increment(-amt) });

  await batch.commit();
  return txDoc.id;
};

// Refund cuando un miembro se baja de un partido que había pagado con
// tokens del equipo. Devuelve el monto al jugador y al pozo.
export const refundMemberShift = async (
  teamId,
  memberId,
  shiftId,
  amount,
  actor,
  note = '',
) => {
  if (!teamId || !memberId || !shiftId || !amount) return null;
  const amt = Number(amount);
  if (!(amt > 0)) return null;
  const batch = writeBatch(db);

  const txDoc = doc(walletTxRef(teamId));
  batch.set(txDoc, buildTxDoc({
    type: 'refund',
    direction: 'in',
    amount: amt,
    memberId,
    method: 'team_wallet',
    relatedShiftId: shiftId,
    note,
    actor,
  }));

  batch.set(memberBalanceRef(teamId, memberId), {
    userId: memberId,
    balance: increment(amt),
    totalSpent: increment(-amt),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  batch.update(teamRef(teamId), { walletBalance: increment(amt) });

  await batch.commit();
  return txDoc.id;
};

// Gasto/ingreso manual del equipo (compra de material, alquiler de
// cancha pagado por fuera del wallet de miembros, etc). No afecta
// memberBalances.
export const addTeamWalletExpense = async (teamId, { concept, amount, note }, actor) => {
  const amt = Number(amount);
  if (!teamId || !(amt > 0)) return null;
  const batch = writeBatch(db);
  const txDoc = doc(walletTxRef(teamId));
  batch.set(txDoc, buildTxDoc({
    type: 'expense',
    direction: 'out',
    amount: amt,
    concept: concept ?? '',
    note: note ?? '',
    actor,
  }));
  batch.update(teamRef(teamId), { walletBalance: increment(-amt) });
  await batch.commit();
  return txDoc.id;
};

export const addTeamWalletIncome = async (teamId, { concept, amount, note }, actor) => {
  const amt = Number(amount);
  if (!teamId || !(amt > 0)) return null;
  const batch = writeBatch(db);
  const txDoc = doc(walletTxRef(teamId));
  batch.set(txDoc, buildTxDoc({
    type: 'income',
    direction: 'in',
    amount: amt,
    concept: concept ?? '',
    note: note ?? '',
    actor,
  }));
  batch.update(teamRef(teamId), { walletBalance: increment(amt) });
  await batch.commit();
  return txDoc.id;
};

// Subscripciones en tiempo real ----------------------------------------
export const onTeamMemberBalancesChange = (teamId, setSnapshot) => {
  if (!teamId) return () => {};
  const ref = collection(db, 'teams', teamId, 'memberBalances');
  return onSnapshot(
    ref,
    setSnapshot,
    err => {
      if (err.code !== 'permission-denied') {
        console.error('[Firestore] memberBalances listener:', err.code, err.message);
      }
    },
  );
};

export const onTeamWalletTxChange = (teamId, setSnapshot, { limit: max = 100 } = {}) => {
  if (!teamId) return () => {};
  const ref = query(
    collection(db, 'teams', teamId, 'walletTx'),
    orderBy('createdAt', 'desc'),
    fsLimit(max),
  );
  return onSnapshot(
    ref,
    setSnapshot,
    err => {
      if (err.code !== 'permission-denied') {
        console.error('[Firestore] walletTx listener:', err.code, err.message);
      }
    },
  );
};

// Lee todos los equipos donde el usuario es miembro y trae su
// memberBalance. Devuelve { team, balance }.
export const fetchUserTeamBalances = async userId => {
  if (!userId) return [];
  const teams = await fetchByTable({
    tableName: 'teams',
    whereCondition: where('memberUserIds', 'array-contains', userId),
  });
  const rows = await Promise.all((teams ?? []).map(async team => {
    try {
      const snap = await getDoc(memberBalanceRef(team.id, userId));
      const data = snap.exists() ? snap.data() : null;
      return { team, balance: data };
    } catch {
      return { team, balance: null };
    }
  }));
  return rows;
};

// Lee el saldo puntual de un miembro dentro de un equipo. Devuelve
// null si nunca se movió.
export const fetchMemberBalance = async (teamId, userId) => {
  if (!teamId || !userId) return null;
  try {
    const snap = await getDoc(memberBalanceRef(teamId, userId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch {
    return null;
  }
};

export const roleOf = (team, userId) => {
  if (!team || !userId) return null;
  if (team.captainUserId === userId) return 'captain';
  if ((team.managerUserIds ?? []).includes(userId)) return 'manager';
  if ((team.memberUserIds ?? []).includes(userId)) return 'member';
  return null;
};
